import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Layers, Wrench, ShieldCheck, Cpu, Monitor, Zap } from 'lucide-react'

export type FeaturesProps = Record<string, never>
export const Features = ({}: FeaturesProps) => {
  return (
    <>
      <section id="features" className="mb-16 scroll-mt-24" aria-labelledby="features-heading">
        <h2
          id="features-heading"
          className="text-3xl font-semibold mb-12 text-center text-foreground"
        >
          Key Features
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card className="group hover:border-primary transition-colors">
            <CardContent className="pt-6">
              <div className="mb-4 p-3 rounded-full bg-primary/10 w-fit">
                <Layers className="w-6 h-6 text-foreground" />
              </div>
              <h4 className="text-xl font-semibold mb-2 text-foreground">
                Universal
              </h4>
              <p className="text-muted-foreground">
                One npm package for Android, iOS, Web, and macOS. Works with Expo and React Native
                CLI.
              </p>
            </CardContent>
          </Card>
          <Card className="group hover:border-primary transition-colors">
            <CardContent className="pt-6">
              <div className="mb-4 p-3 rounded-full bg-primary/10 w-fit">
                <Wrench className="w-6 h-6 text-foreground" />
              </div>
              <h4 className="text-xl font-semibold mb-2 text-foreground">
                Easier setup
              </h4>
              <p className="text-muted-foreground">
                <a
                  href="https://react-native-google-signin.github.io/docs/config-doctor"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Config Doctor
                </a>{' '}
                and parameter{' '}
                <a
                  href="https://react-native-google-signin.github.io/docs/one-tap#automatic-config"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  auto-detection
                </a>{' '}
                help you avoid wasting hours debugging.
              </p>
            </CardContent>
          </Card>

          <Card className="group hover:border-primary transition-colors">
            <CardContent className="pt-6">
              <div className="mb-4 p-3 rounded-full bg-primary/10 w-fit">
                <ShieldCheck className="w-6 h-6 text-foreground" />
              </div>
              <h4 className="text-xl font-semibold mb-2 text-foreground">
                Advanced security
              </h4>
              <p className="text-muted-foreground">
                Production-grade extras like custom nonce (handy for Supabase Auth) and iOS App
                Check. Details in the{' '}
                <a
                  href="https://react-native-google-signin.github.io/docs/security"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  security docs
                </a>
                .
              </p>
            </CardContent>
          </Card>

          <Card className="group hover:border-primary transition-colors">
            <CardContent className="pt-6">
              <div className="mb-4 p-3 rounded-full bg-primary/10 w-fit">
                <Zap className="w-6 h-6 text-foreground" />
              </div>
              <h4 className="text-xl font-semibold mb-2 text-foreground">
                Credential Manager (Android)
              </h4>
              <p className="text-muted-foreground">
                Built on the modern Google identity APIs, replacing the legacy Android sign-in stack
                from the free version.{' '}
                <a
                  href="#compare"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                >
                  Compare versions
                </a>
                .
              </p>
            </CardContent>
          </Card>

          <Card className="group hover:border-primary transition-colors">
            <CardContent className="pt-6">
              <div className="mb-4 p-3 rounded-full bg-primary/10 w-fit">
                <Cpu className="w-6 h-6 text-foreground" />
              </div>
              <h4 className="text-xl font-semibold mb-2 text-foreground">
                New architecture ready
              </h4>
              <p className="text-muted-foreground">
                Compatible with both the old and new React Native architecture, across wide range of
                supported Expo & React Native versions.
              </p>
            </CardContent>
          </Card>

          <Card className="group hover:border-primary transition-colors">
            <CardContent className="pt-6">
              <div className="mb-4 p-3 rounded-full bg-primary/10 w-fit">
                <Monitor className="w-6 h-6 text-foreground" />
              </div>
              <h4 className="text-xl font-semibold mb-2 text-foreground">
                Web + macOS included
              </h4>
              <p className="text-muted-foreground">
                Ship the same sign-in experience across native, web, and desktop without swapping
                libraries.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </>
  )
}
